// Ported from design/project/App22-IncomingChallenge.dc.html, then wired: someone opened a challenge link
// (t.me/bot?startapp=c_<id>). Accepting locks their stake and the battle starts at once; too late goes to App22b,
// run out or called off to App22c.
import { useEffect, useState } from "react";
import { usdt } from "@degenduel/shared";
import { api, ApiError } from "../api.js";
import { useGame, useTicker } from "../game.js";
import { go } from "../router.js";
import { play } from "../sound.js";
import { arenaLabel, lastRound, openedChallenge, otherSide, setOpenedChallenge, stakeLabel, type Challenge } from "../challenge.js";
import { HeadToHead, leadNote } from "./Rivalry.js";

/** The challenge App22b and App22c talk about, kept for them when this screen hands over. */
export const SEEN = "dd:seenChallenge";
export function seenChallenge(): Challenge | null {
  try {
    return JSON.parse(sessionStorage.getItem(SEEN) ?? "null") as Challenge | null;
  } catch {
    return null;
  }
}

const ERRORS: Record<string, string> = {
  already_in_battle: "Finish your battle first, then take it.",
  already_queued: "You are searching for an opponent. Cancel the search first.",
  challenge_open: "You have your own challenge open. Cancel it first.",
  stake_not_allowed: "This stake is above your limit for now.",
  arena_locked: "This arena isn't open yet, so the challenge closed.",
  arena_closed: "This arena is closed right now, so the challenge closed.",
  account_frozen: "Your account is paused while we check something. Your balance is safe and withdrawals still work.",
  on_break: "You're on a break from battling for now. Your balance is safe and withdrawals still work.",
  daily_limit: "That would go over your daily stake limit. It resets at 00:00 WAT.",
  maintenance: "Quick upgrade in progress. Try again in a few minutes.",
  network: "No connection. Check your data and try again.",
};

export function App22_IncomingChallenge() {
  const { me, now, acceptChallenge, declineRematch, topUp, challenge: mine } = useGame();
  useTicker(500);
  const id = openedChallenge();
  const [c, setC] = useState<Challenge | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handOver = (to: "App22b" | "App22c", seen: Challenge) => {
    sessionStorage.setItem(SEEN, JSON.stringify(seen));
    setOpenedChallenge(null);
    go(to, true);
  };

  useEffect(() => {
    if (!id) return void go("App09", true);
    let stop = false;
    const load = () =>
      api<{ challenge: Challenge }>(`/v1/challenges/${id}`)
        .then(({ challenge }) => !stop && setC(challenge))
        .catch((e) => {
          if (stop) return;
          if (e instanceof ApiError && e.status === 404) {
            setOpenedChallenge(null);
            go("App09", true);
          }
        });
    void load();
    // Taken or called off while the player is still deciding: the screen says so.
    const t = setInterval(load, 4_000);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, [id]);

  const myId = me?.user.id;
  useEffect(() => {
    if (!c || !myId) return;
    if (c.creator.id === myId) {
      // Their own link: back to its waiting screen while it's open, else the lobby.
      setOpenedChallenge(null);
      go(mine?.id === c.id && c.state === "open" ? "App21b" : "App09", true);
    } else if (c.state === "accepted" && c.kind === "rematch") {
      // Taken already, by this player (a rematch has no one else): the battle is under way or done.
      setOpenedChallenge(null);
      go("App09", true);
    } else if (c.state === "accepted") handOver("App22b", c);
    else if (c.state !== "open") handOver("App22c", c);
  }, [c?.state, myId]);

  const left = c ? Math.max(0, c.expiresAt - now()) : 0;
  useEffect(() => {
    if (c?.state === "open" && left === 0) handOver("App22c", { ...c, state: "expired" });
  }, [left === 0, c?.state]);

  if (!c) return <div className="screen" />;
  const stake = stakeLabel(c.stake);
  const take = otherSide(c.side);
  const canAfford = !!me && usdt(me.balance.available) >= usdt(c.stake);
  const initials = c.creator.name.replace(/^@/, "").slice(0, 2).toUpperCase();
  const clock = `${Math.floor(left / 60_000)}:${String(Math.floor((left % 60_000) / 1000)).padStart(2, "0")}`;
  const minutes = c.durationSec === 60 ? "1 minute" : "3 minutes";

  const accept = async () => {
    if (busy || !me) return;
    if (!canAfford) return void topUp();
    play("tap");
    setBusy(true);
    setError(null);
    try {
      await acceptChallenge(c.id);
      setOpenedChallenge(null);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : "network";
      if (code === "taken") return handOver("App22b", { ...c, state: "accepted" });
      if (code === "expired" || code === "cancelled" || code === "declined") return handOver("App22c", { ...c, state: code === "expired" ? "expired" : "cancelled" });
      if (code === "own_challenge") return go("App21b", true);
      if (code === "join_channel" && e instanceof ApiError) return go(e.detail.left ? "App73" : "App72");
      if (code === "low_balance") setError(`You need ${stake} USDT to take this.`);
      else setError(ERRORS[code] ?? "Something went wrong. Try again.");
      setBusy(false);
    }
  };
  const notToday = () => {
    // A rematch is for this player only: saying no hands the sender's stake back now, not in 5 minutes.
    if (c.kind === "rematch") void declineRematch(c.id);
    setOpenedChallenge(null);
    go("App09", true);
  };
  // A rematch from the bot's revenge message: same screen, with the rivalry instead of a stranger's call.
  const rival = c.kind === "rematch" ? c.rivalry : undefined;
  const revenge = rival?.lastWinner === "target";

  return (
    <>
      <div className="screen">
  <div className="htR" aria-hidden="true" style={{ position: "absolute", left: "0", top: "0", width: "100%", height: "470px", clipPath: "polygon(0 0, 100% 0, 100% 78%, 0 100%)" }}></div>
  <svg aria-hidden="true" width="390" height="470" viewBox="0 0 390 470" style={{ position: "absolute", left: "50%", marginLeft: "-195px", top: "0" }}>
    <g stroke="#FFFFFF" strokeOpacity="0.18" strokeWidth="3">
      <line x1="195" y1="230" x2="0" y2="40"></line><line x1="195" y1="230" x2="80" y2="0"></line><line x1="195" y1="230" x2="310" y2="0"></line>
      <line x1="195" y1="230" x2="390" y2="40"></line><line x1="195" y1="230" x2="390" y2="220"></line><line x1="195" y1="230" x2="0" y2="220"></line>
    </g>
  </svg>

  <div className="pad squeeze" style={{ height: "100%", paddingBottom: "calc(var(--dd-safe-bottom) + 8px)", boxSizing: "border-box", display: "flex", flexDirection: "column" }}>
    <div className="row tgbar" style={{ position: "absolute", left: "0", right: "0", justifyContent: "center" }}>
      <span className="tag tagW">{c.kind === "rematch" ? "FROM THE BOT" : "FROM A CHAT LINK"}</span>
    </div>

    <div style={{ marginTop: "24px", alignSelf: "center", textAlign: "center", transform: "skewX(-8deg)" }}>
      <div className="big" style={{ fontSize: "30px", color: "#FFFFFF" }}>{rival ? (revenge ? "REVENGE" : "REMATCH") : "A NEW"}</div>
      <div className="big" style={{ fontSize: "34px", color: "#FF3366", textShadow: "4px 4px 0 #0B0E14, 8px 8px 0 #CCFF00" }}>{rival ? `ROUND ${rival.rounds + 1}` : "CHALLENGER"}</div>
    </div>

    <div style={{ marginTop: "28px", alignSelf: "center", position: "relative" }}>
      <div className="avA" style={{ width: "120px", height: "120px", borderRadius: "28px", borderWidth: "4px", background: c.side === "long" ? "#CCFF00" : "#FF3366", fontSize: "34px", boxShadow: "8px 8px 0 #0B0E14", animation: "ddPop 1.4s infinite" }}>{initials}</div>
      <span className={c.side === "long" ? "tag tagL" : "tag tagR"} style={{ position: "absolute", left: "50%", bottom: "-14px", transform: "translateX(-50%)", whiteSpace: "nowrap" }}>{c.side === "long" ? "LONG ▲" : "SHORT ▼"}</span>
    </div>
    <span className="unb" style={{ marginTop: "26px", alignSelf: "center", fontWeight: "900", fontSize: "18px", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.creator.name.toUpperCase()}</span>
    <span className="muted" style={{ marginTop: "4px", alignSelf: "center", fontSize: "13px" }}>{rival ? `${lastRound(c)} and ${revenge ? "wants it back" : "wants another go"}` : `thinks ${arenaLabel(c.arena)} ${c.side === "long" ? "pumps" : "drops"} in ${minutes}`}</span>
    {rival && <div style={{ marginTop: "14px", alignSelf: "center", maxWidth: "100%" }}><HeadToHead them={c.creator.name} mine={rival.targetWins} theirs={rival.creatorWins} note={leadNote(rival.targetWins, rival.creatorWins)} /></div>}

    <div className="panel" style={{ marginTop: rival ? "14px" : "22px", display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", padding: "14px 0" }}>
      <div style={{ textAlign: "center" }}><div className="unb muted" style={{ fontSize: "9px", fontWeight: "800" }}>YOU TAKE</div><div className={take === "long" ? "mono lime" : "mono red"} style={{ fontWeight: "800", fontSize: "15px", marginTop: "4px" }}>{take === "long" ? "▲ LONG" : "▼ SHORT"}</div></div>
      <div style={{ textAlign: "center", borderLeft: "2px solid #30363D", borderRight: "2px solid #30363D" }}><div className="unb muted" style={{ fontSize: "9px", fontWeight: "800" }}>STAKE</div><div className="mono" style={{ fontWeight: "800", fontSize: "15px", marginTop: "4px" }}>{stake} USDT</div></div>
      <div style={{ textAlign: "center" }}><div className="unb muted" style={{ fontSize: "9px", fontWeight: "800" }}>EXPIRES</div><div className="mono red" style={{ fontWeight: "800", fontSize: "15px", marginTop: "4px" }}>{clock}</div></div>
    </div>

    <div style={{ marginTop: "auto", paddingTop: "16px", display: "flex", flexDirection: "column", gap: "14px" }}>
      {error && <span role="alert" className="red" style={{ textAlign: "center", fontSize: "13px" }}>{error}</span>}
      <button className="btnA" disabled={busy || !me} onClick={() => void accept()}>{!me ? "CONNECTING..." : busy ? "LOCKING STAKE..." : canAfford ? "ACCEPT AND FIGHT" : `TOP UP TO TAKE IT`}</button>
      <button className="btnAS" onClick={notToday}>NOT TODAY</button>
    </div>
  </div>
</div>
    </>
  );
}
